import {useState} from 'react';
import styled from 'styled-components';
import {useLogTimeToTask} from '../hooks/useLogTimeToTask';
import {useWorkRoles} from '../hooks/useWorkRoles';
import {parseTimeInput} from '../utils/parseTimeInput';

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 50;
	display: flex;
	align-items: center;
	justify-content: center;
`;
const Backdrop = styled.div`
	position: absolute;
	inset: 0;
	background: rgb(0 0 0 / 0.4);
`;
const Dialog = styled.div`
	position: relative;
	z-index: 10;
	width: 100%;
	max-width: 28rem;
	border-radius: 12px;
	background: #ffffff;
	padding: 24px;
	box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1);
`;
const Header = styled.div`
	margin-bottom: 16px;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;
const Title = styled.h2`
	margin: 0;
	font-size: 18px;
	line-height: 28px;
	font-weight: 500;
	color: #0f172a;
`;
const CloseButton = styled.button`
	border: 0;
	border-radius: 4px;
	background: transparent;
	padding: 4px;
	color: #94a3b8;
	cursor: pointer;
	&:hover {
		background: #f1f5f9;
		color: #475569;
	}
`;
const LoggingToBlock = styled.div`
	margin-bottom: 16px;
`;
const LoggingToLabel = styled.p`
	margin: 0 0 4px;
	font: ${p => p.theme.ticketing.font.body3};
	color: #64748b;
`;
const LoggingToChip = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border-radius: 6px;
	background: #eef2ff;
	padding: 4px 10px;
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	color: #3730a3;
`;
const Field = styled.div`
	margin-bottom: 16px;
`;
const FieldLabel = styled.label`
	display: block;
	margin-bottom: 4px;
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	color: #334155;
`;
const fieldStyles = `
	width: 100%;
	border-radius: 4px;
	border: 1px solid #e2e8f0;
	padding: 8px 12px;
	box-sizing: border-box;
`;
const TextInput = styled.input`
	${fieldStyles}
	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px #3b82f6;
	}
`;
const StyledSelect = styled.select`
	${fieldStyles}
	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px #3b82f6;
	}
`;
const TextArea = styled.textarea`
	${fieldStyles}
	min-height: 64px;
	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px #3b82f6;
	}
`;
const ErrorText = styled.p`
	margin: 0 0 12px;
	font: ${p => p.theme.ticketing.font.body3};
	color: #dc2626;
`;
const Actions = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 8px;
`;
const SecondaryButton = styled.button`
	border: 1px solid #e2e8f0;
	border-radius: 6px;
	background: #ffffff;
	padding: 8px 16px;
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	color: #334155;
	cursor: pointer;
	&:hover {
		background: #f8fafc;
	}
`;
const PrimaryButton = styled.button`
	border: 0;
	border-radius: 6px;
	background: #4f47d6;
	padding: 8px 16px;
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	color: #ffffff;
	cursor: pointer;
	&:hover {
		background: #3d36b3;
	}
	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

export interface LogTimeModalTask {
	id: string;
	name: string;
	projectId: string | null;
}

export interface LogTimeModalProps {
	open: boolean;
	personId: string;
	onClose: () => void;
	onSuccess: () => void;
	/** Preset target task (Tasks-tab per-card usage). When omitted, `tasks` must be provided. */
	taskId?: string;
	projectId?: string | null;
	taskName?: string;
	/** Selectable tasks (Time-tab usage). A picker is shown when no `taskId` is preset. */
	tasks?: LogTimeModalTask[];
	/**
	 * The ticket the time is being logged through. Passed on so the registration is
	 * attributed to this ticket and appears on its Time tab rather than counting
	 * only as task time.
	 */
	ticketId?: string;
}

/** Local date as YYYY-MM-DD for the date input's default value. */
function todayIso(): string {
	const now = new Date();
	const y = now.getFullYear();
	const m = String(now.getMonth() + 1).padStart(2, '0');
	const d = String(now.getDate()).padStart(2, '0');
	return `${y}-${m}-${d}`;
}

export function LogTimeModal({
	open,
	taskId,
	projectId,
	taskName,
	personId,
	onClose,
	onSuccess,
	tasks,
	ticketId,
}: LogTimeModalProps) {
	const [time, setTime] = useState('');
	const [date, setDate] = useState(todayIso());
	const [roleId, setRoleId] = useState('');
	const [note, setNote] = useState('');
	const [pickedTaskId, setPickedTaskId] = useState('');
	const roles = useWorkRoles();
	const {submit, loading, error} = useLogTimeToTask();

	if (!open) return null;

	// A preset task (Tasks-tab usage) wins; otherwise resolve from the picker.
	const showPicker = !taskId && !!tasks;
	const pickedTask = tasks?.find(t => t.id === pickedTaskId) ?? null;
	const resolvedTaskId = taskId ?? pickedTask?.id ?? null;
	const resolvedProjectId = taskId ? projectId ?? null : pickedTask?.projectId ?? null;
	const resolvedTaskName = taskName ?? pickedTask?.name ?? '';

	const hours = parseTimeInput(time);
	const minutes = hours == null ? null : Math.round(hours * 60);
	const canSubmit = minutes != null && minutes > 0 && !!resolvedTaskId && !loading;

	const handleSubmit = () => {
		if (minutes == null || minutes <= 0 || !resolvedTaskId) return;
		const [year, month, day] = date.split('-').map(Number);
		submit({
			taskId: resolvedTaskId,
			projectId: resolvedProjectId,
			year,
			month,
			day,
			minutesRegistered: minutes,
			roleId: roleId || undefined,
			notes: note.trim() || undefined,
			personId,
			ticketId,
		}).then(
			() => {
				onSuccess();
				onClose();
			},
			() => {}
		);
	};

	return (
		<Overlay role="dialog" aria-modal="true" aria-label="Log time">
			<Backdrop onClick={onClose} />
			<Dialog>
				<Header>
					<Title>Log time</Title>
					<CloseButton type="button" aria-label="Close" onClick={onClose}>
						✕
					</CloseButton>
				</Header>

				{showPicker ? (
					<Field>
						<FieldLabel htmlFor="log-time-task">Task</FieldLabel>
						<StyledSelect id="log-time-task" value={pickedTaskId} onChange={e => setPickedTaskId(e.target.value)}>
							<option value="">Select a task…</option>
							{tasks?.map(t => (
								<option key={t.id} value={t.id}>
									{t.name}
								</option>
							))}
						</StyledSelect>
					</Field>
				) : (
					<LoggingToBlock>
						<LoggingToLabel>Logging to</LoggingToLabel>
						<LoggingToChip>{resolvedTaskName}</LoggingToChip>
					</LoggingToBlock>
				)}

				{error && <ErrorText role="alert">{error}</ErrorText>}

				<Field>
					<FieldLabel htmlFor="log-time-time">Time</FieldLabel>
					<TextInput
						id="log-time-time"
						type="text"
						placeholder="e.g. 1.5 or 90m"
						value={time}
						onChange={e => setTime(e.target.value)}
					/>
				</Field>

				<Field>
					<FieldLabel htmlFor="log-time-date">Date</FieldLabel>
					<TextInput id="log-time-date" type="date" value={date} onChange={e => setDate(e.target.value)} />
				</Field>

				<Field>
					<FieldLabel htmlFor="log-time-role">Role</FieldLabel>
					<StyledSelect id="log-time-role" value={roleId} onChange={e => setRoleId(e.target.value)}>
						<option value="">No role</option>
						{roles.map(r => (
							<option key={r.id} value={r.id}>
								{r.name}
							</option>
						))}
					</StyledSelect>
				</Field>

				<Field>
					<FieldLabel htmlFor="log-time-note">Note</FieldLabel>
					<TextArea id="log-time-note" value={note} onChange={e => setNote(e.target.value)} />
				</Field>

				<Actions>
					<SecondaryButton type="button" onClick={onClose}>
						Cancel
					</SecondaryButton>
					<PrimaryButton type="button" disabled={!canSubmit} onClick={handleSubmit}>
						Log time
					</PrimaryButton>
				</Actions>
			</Dialog>
		</Overlay>
	);
}
